// pages/LandingPage.jsx
// Main landing page — matches CW Expense Manager aesthetic

import React from 'react';
import { useMsal } from '@azure/msal-react';
import { getGroups, isAdmin, hasGroupAccess } from '../auth';
import { DASHBOARDS } from '../dashboards';
import DashboardTile from '../components/DashboardTile';

export default function LandingPage() {
  const { accounts } = useMsal();
  const account = accounts[0];
  const firstName = account?.name?.split(' ')[0] || 'there';

  const userIsAdmin = isAdmin();
  const userGroups = getGroups();

  const visibleDashboards = DASHBOARDS.filter((d) => {
    if (userIsAdmin) return true;
    if (!d.available) {
      return d.groupId && userGroups.includes(d.groupId);
    }
    return d.groupId && hasGroupAccess(d.groupId);
  });

  return (
    <div style={{ position: 'relative', minHeight: 'calc(100vh - 56px)' }}>
      {/* Dot-grid background */}
      <div className="blueprint-grid" />

      <div style={{
        position: 'relative',
        zIndex: 1,
        maxWidth: 960,
        margin: '0 auto',
        padding: '2.5rem 1.5rem',
      }}>
        {/* Welcome section */}
        <div style={{
          marginBottom: '2rem',
          animation: 'fadeUp 0.45s cubic-bezier(0.22, 1, 0.36, 1) both',
        }}>
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.65rem',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            marginBottom: '0.35rem',
          }}>
            Welcome back
          </div>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.6rem',
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}>
            {firstName}
          </h2>
          <div style={{
            width: '36px',
            height: '3px',
            background: 'var(--accent-bold)',
            borderRadius: '2px',
            marginTop: '0.6rem',
          }} />
        </div>

        {/* Section label */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '1rem',
          animation: 'fadeUp 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.08s both',
        }}>
          <div style={{
            width: '3px',
            height: '14px',
            background: 'var(--accent-bold)',
            borderRadius: '2px',
            flexShrink: 0,
          }} />
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.6rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--text-secondary)',
            fontWeight: 500,
          }}>
            Your Dashboards
          </span>
        </div>

        {/* Dashboard grid */}
        {visibleDashboards.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1rem',
          }}>
            {visibleDashboards.map((d, i) => (
              <DashboardTile
                key={d.id}
                name={d.name}
                description={d.description}
                url={d.url}
                icon={d.icon}
                available={d.available}
                index={i}
              />
            ))}
          </div>
        ) : (
          <div style={{
            padding: '2.5rem',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            textAlign: 'center',
            animation: 'fadeUp 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both',
          }}>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.85rem',
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
            }}>
              You don't have access to any dashboards yet.
              <br />
              <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                Contact your administrator.
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
